import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Alert, Box, Container, Grid, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const dateLocale = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const today = () => dateLocale(new Date());
const firstDay = () => { const d = new Date(); return dateLocale(new Date(d.getFullYear(), d.getMonth(), 1)); };
const dh = (n) => `${Number(n ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DH`;
const frenchDate = (s) => s ? s.split('-').reverse().join('/') : '';
const HeaderCell = styled(TableCell)(({ theme }) => ({ backgroundColor: theme.palette.common.black, color: theme.palette.common.white, fontWeight: 600 }));

export default function Caisse() {
  const { id, apitoken, type } = useSelector(state => state.userData);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [debut, setDebut] = useState(firstDay);
  const [fin, setFin] = useState(today);
  const [data, setData] = useState({ jours: [], totaux: { espece: 0, cheque: 0, banque: 0, total: 0 } });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const periodeValide = Boolean(debut && fin && debut <= fin);
  const autorise = type === 'admin' || type === 'manager';

  useEffect(() => {
    if (!autorise || !periodeValide) { setData({ jours: [], totaux: { espece: 0, cheque: 0, banque: 0, total: 0 } }); return; }
    const controller = new AbortController();
    setLoading(true); setError('');
    axios.post(`${apiUrl}caisse/${id}/${apitoken}/${debut}/${fin}`, {}, { signal: controller.signal, timeout: 20000 })
      .then(({ data }) => setData(data))
      .catch(err => { if (err.code !== 'ERR_CANCELED') setError(err.response?.data?.detail || 'Impossible de charger la caisse.'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [apiUrl, id, apitoken, debut, fin, periodeValide, autorise]);

  const lignes = useMemo(() => data.jours || [], [data.jours]);
  const totaux = data.totaux || {};
  if (!autorise) return <Container maxWidth="lg" sx={{ mt: 5 }}><Alert severity="error">Accès réservé au manager et à l'administrateur.</Alert></Container>;
  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 6 }}>
      <Typography variant="h4" align="center" color="primary" sx={{ mb: 4 }}>Caisse du manager</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}><TextField fullWidth type="date" label="Date début" value={debut} onChange={e => setDebut(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
        <Grid item xs={12} sm={6} md={3}><TextField fullWidth type="date" label="Date fin" value={fin} onChange={e => setFin(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
      </Grid>
      {!periodeValide && <Alert severity="warning" sx={{ mb: 2 }}>Sélectionne une période valide.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'action.hover' }} elevation={0}>
        <Typography color="text.secondary" variant="body2">Total réellement encaissé sur la période</Typography>
        <Typography variant="h4" fontWeight={700}>{dh(totaux.total)}</Typography>
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Typography variant="body2">Espèces : <strong>{dh(totaux.espece)}</strong></Typography>
          <Typography variant="body2">Chèques : <strong>{dh(totaux.cheque)}</strong></Typography>
          <Typography variant="body2">Banque : <strong>{dh(totaux.banque)}</strong></Typography>
        </Stack>
      </Paper>
      <TableContainer component={Paper}>
        {loading && <LinearProgress />}
        <Table sx={{ minWidth: 700 }}>
          <TableHead><TableRow>
            <HeaderCell>Date</HeaderCell><HeaderCell align="right">Espèces</HeaderCell><HeaderCell align="right">Chèques</HeaderCell><HeaderCell align="right">Banque</HeaderCell><HeaderCell align="right">Total</HeaderCell>
          </TableRow></TableHead>
          <TableBody>
            {!loading && lignes.length === 0 && <TableRow><TableCell colSpan={5} align="center">Aucun versement sur cette période</TableCell></TableRow>}
            {!loading && lignes.map((j, i) => <TableRow key={j.date} sx={{ bgcolor: i % 2 === 0 ? 'action.hover' : 'inherit' }}>
              <TableCell>{frenchDate(j.date)}</TableCell><TableCell align="right">{dh(j.espece)}</TableCell><TableCell align="right">{dh(j.cheque)}</TableCell><TableCell align="right">{dh(j.banque)}</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>{dh(j.total)}</TableCell>
            </TableRow>)}
            {!loading && <TableRow sx={{ bgcolor: 'action.selected' }}>
              <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>{dh(totaux.espece)}</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>{dh(totaux.cheque)}</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>{dh(totaux.banque)}</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>{dh(totaux.total)}</TableCell>
            </TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>Somme des versements enregistrés, sans déduction des dépenses ou sorties de caisse.</Typography>
    </Container>
  );
}
